import type { ServiceLifecycle } from '../services/service-lifecycle.js'

type WebLifecyclePhase = 'active' | 'suspended'

type WebLifecycleEventTarget = {
    addEventListener(type: string, listener: () => void): void
    removeEventListener(type: string, listener: () => void): void
}

type WebLifecycleDocument = WebLifecycleEventTarget & {
    visibilityState: DocumentVisibilityState
}

export type WebLifecycleBridgeOptions = {
    document: WebLifecycleDocument
    lifecycle: Pick<ServiceLifecycle, 'resume' | 'suspend'>
    reportError?: (error: unknown) => void
    window: WebLifecycleEventTarget
}

export class WebLifecycleBridge {
    readonly #document: WebLifecycleDocument
    readonly #lifecycle: Pick<ServiceLifecycle, 'resume' | 'suspend'>
    readonly #reportError: (error: unknown) => void
    readonly #window: WebLifecycleEventTarget
    #phase: WebLifecyclePhase | undefined
    #queue = Promise.resolve()
    #started = false

    public constructor(options: WebLifecycleBridgeOptions) {
        this.#document = options.document
        this.#lifecycle = options.lifecycle
        this.#reportError =
            options.reportError ??
            ((error) => {
                console.warn('Unable to apply web service lifecycle transition.', error)
            })
        this.#window = options.window
    }

    public start(): void {
        if (this.#started) {
            return
        }

        this.#started = true
        const initialPhase = this.#document.visibilityState === 'hidden' ? 'suspended' : 'active'
        this.#phase = initialPhase
        this.#document.addEventListener('visibilitychange', this.#handleVisibilityChange)
        this.#window.addEventListener('pageshow', this.#handlePageShow)
        this.#window.addEventListener('pagehide', this.#handlePageHide)

        if (initialPhase === 'suspended') {
            this.#queue = this.#applyPhase(initialPhase)
        }
    }

    public stop(): void {
        if (!this.#started) {
            return
        }

        this.#started = false
        this.#document.removeEventListener('visibilitychange', this.#handleVisibilityChange)
        this.#window.removeEventListener('pageshow', this.#handlePageShow)
        this.#window.removeEventListener('pagehide', this.#handlePageHide)
        this.#phase = undefined
    }

    public async flush(): Promise<void> {
        await this.#queue
    }

    readonly #handleVisibilityChange = (): void => {
        this.#transition(this.#document.visibilityState === 'hidden' ? 'suspended' : 'active')
    }

    readonly #handlePageShow = (): void => {
        this.#transition('active')
    }

    readonly #handlePageHide = (): void => {
        this.#transition('suspended')
    }

    #transition(phase: WebLifecyclePhase): void {
        if (!this.#started || phase === this.#phase) {
            return
        }

        this.#phase = phase
        this.#queue = this.#queue.then(async () => await this.#applyPhase(phase))
    }

    async #applyPhase(phase: WebLifecyclePhase): Promise<void> {
        try {
            if (phase === 'active') {
                await this.#lifecycle.resume()
            } else {
                await this.#lifecycle.suspend()
            }
        } catch (error) {
            this.#reportError(error)
        }
    }
}
